// Data-plausibility layer — REDESIGNED 2026-07-09 (the 762-row lesson).
//
// The old design flagged entities into a human vetting queue whenever any
// input looked odd. At eleven markets that queue hit 762 in-scope rows; at
// 27 it would be thousands — a review queue nobody can work is not a safety
// mechanism. The redesign makes every state MACHINE-RESOLVED:
//
//   1. INPUT SANITIZATION (sanitizeEmployees) — when the ratio between
//      headcount and financials is impossible, the EMPLOYEES field is the
//      broken side (iXBRL context-date leaks, partial-FTE fields like NL
//      DigiMV where a €2.2bn UMC "has 142 employees", headcount bleeding
//      into micro filings). Null the input and serve through the ladder on
//      the surviving predictors. The entity gets an honest lower-mechanism
//      value — never a fence, never a queue entry.
//
//   2. MODELLED CEILING → PENDING (served-market.ts) — a modelled value
//      above €120M is never served OR fenced: the row goes modelled-pending
//      (awaiting financials). A genuine €120M+ operator files statements
//      somewhere; when they land, reported-wins fills the row automatically
//      (the SE majors PDF-pull pattern). D5: never quarantine the entity.
//
//   3. REPORTED SCALE-LEAK FENCE (evaluateQuarantine) — the only remaining
//      quarantine: a REPORTED figure above its subsegment's generous ceiling
//      (the Harley Academy case — £2.79M filed, £2.79B parsed via a scale=3
//      attribute leak). Order-of-magnitude leaks only; the ceilings sit 2-5×
//      above the largest legitimate operator per subsegment, so the queue
//      holds a handful of genuine anomalies platform-wide, not hundreds of
//      false positives.
//
// Dormant rows are skipped — their £0 turnover is genuine. Out-of-scope
// rows below the €150k gate are invisible to the screener and are not
// worth fencing at all.
//
// This module is the canonical source of the rules. Consumers:
//   - packages/normalised-metrics/src/registry/served-market.ts (live path)
//   - packages/normalised-metrics/src/recompute.ts (legacy lane)
//   - apps/web/src/workspace/row-quality.ts (render-time fallback)
// The Python mirror (scripts/import_uk_healthcare_companies.py) implements
// the RETIRED rules — rows it flags are re-evaluated at the next recompute,
// so it needs no urgent sync; retire it with the importer.

export type QuarantineReason = 'reported_scale_leak';

/** Override-driven hold (never emitted by evaluateQuarantine): a curated,
 *  named-entity hold applied via entity_data_overrides — a reported figure
 *  whose entity-vs-group provenance is unverified is kept out of the clean
 *  board until the source confirms (SE majors, handover 2026-07-09). A tiny
 *  explicit list with a documented clearing condition — the antithesis of a
 *  heuristic vetting queue, so consistent with the redesign. */
export type OverrideHoldReason = 'reported_provenance_unverified';

/** Retired reason strings still persisted on rows that predate the
 *  redesign — kept for display until every market recomputes. */
export type LegacyQuarantineReason =
  | 'employees_looks_like_year'
  | 'large_headcount_micro_assets'
  | 'revenue_per_employee_too_high'
  | 'revenue_per_employee_too_low'
  | 'modelled_turnover_implausible'
  | 'asset_scale_implausible';

// ── 1. Input sanitization ────────────────────────────────────────────────

export type EmployeesSanitizeReason =
  /** 1900–2030 in the employees field — an iXBRL context-date leak. */
  | 'employees_year_leak'
  /** Reported revenue per head above €12M — impossible in any healthcare
   *  segment (wholesale tops out ~€8-10M/head), so the employees field is a
   *  partial/wrong count (the NL DigiMV FTE pattern). The REVENUE stands. */
  | 'employees_partial_count'
  /** The headcount is EXACTLY equal to another number on the same entity —
   *  its registration number, its period year, or one of its own monetary
   *  fields. A filing writes one value into the wrong slot; the tell is not
   *  that the count is large, it is that it COLLIDES.
   *
   *  Observed 2026-08-11 across the GB >=12m cohort — every one exact:
   *    JOATMOS          8,252,444 = CRN 08252444
   *    CARROLL MEDICAL    191,731 = equity GBP 191,731
   *    UTOM LTD            24,050 = equity GBP 24,050
   *    KLENAM SERVICES      5,430 = total assets AND equity GBP 5,430
   *    OAKINGHAM CARE       2,025 = period year 2025
   *
   *  This is strictly better than a size threshold: it is definitional rather
   *  than heuristic, and it catches corrupt counts (24,050 / 5,430) that sit
   *  far BELOW any ceiling one could safely set. Genuine large employers do
   *  not collide — G P HOMECARE (2,398 staff, GBP 60M revenue) and BONDCARE
   *  (2,114 staff, GBP 95M revenue) match none of their own figures. */
  | 'employees_collides_with_own_field'
  /** Beyond any conceivable patient-facing headcount. The largest legitimate
   *  counts in the GB universe are care groups at ~2-4k; the biggest private
   *  European operators reach ~76k, but those are pan-European groups, not
   *  a single GB filer. A ceiling at 50k sits >2× above the largest real UK
   *  care employers (HC-One / Four Seasons ~20k) and >12× above the largest
   *  in our own data, and catches the corrupt 6-figure fields (469,000 /
   *  191,731 / 141,100, each against a 5-figure balance sheet). */
  | 'employees_beyond_ceiling';
// NOTE deliberately ABSENT: a headcount-vs-micro-balance-sheet rule. Big
// staff on tiny assets is the NORMAL shape of domiciliary care and staffing
// operators (the first cut of this redesign nulled their headcounts and
// silently degraded €5.1B of legitimate GB emp-model serves to floors).
// Genuinely corrupt headcounts that inflate a modelled value are caught by
// the €120M ceiling downstream; below it they are bounded and calibrated.

export interface SanitizedEmployees {
  employees: number | null;
  reason: EmployeesSanitizeReason | null;
}

/** Impossible-per-head threshold: above this the headcount, not the
 *  revenue, is the corrupted side. The most rev-per-head-intensive genuine
 *  segment (pharma wholesale) tops out ~€8-10M/head; the NL partial-FTE
 *  hospitals sit €15M+. */
const REVENUE_PER_HEAD_IMPOSSIBLE_EUR = 12_000_000;

/** Absolute headcount ceiling — see 'employees_beyond_ceiling'. The
 *  collision rule is the precise instrument; this is the backstop for
 *  corrupt values that happen not to collide (TLC CARE HOMES 469,000,
 *  GIFT A SMILE 141,100). */
const EMPLOYEES_ABSOLUTE_CEILING = 50_000;

/** Below this a collision is plausibly coincidence (a firm with 12 staff and
 *  GBP 12 of something), so the rule only fires above it. Every observed
 *  collision is far above. */
const COLLISION_MIN_VALUE = 500;

/** Sanitize a headcount against the row's financials. Returns the value to
 *  feed BOTH the serve predictors and enm.employees — a corrupt count must
 *  not model revenue OR display. */
export function sanitizeEmployees(
  employees: number | null,
  ctx: {
    /** Latest reported revenue, EUR-normalised where available (native
     *  otherwise — the thresholds are order-of-magnitude, not precise). */
    reportedRevenue?: number | null;
    totalAssets?: number | null;
    /** The entity's own register id (GB: Companies House number). */
    registryId?: string | null;
    /** The filing's period-end year. */
    periodEndYear?: number | null;
    /** Other monetary fields on the SAME filing. A headcount identical to any
     *  of them is a slot mix-up, not a fact — see
     *  'employees_collides_with_own_field'. */
    equity?: number | null;
    revenue?: number | null;
    cashPosition?: number | null;
    debtors?: number | null;
    creditors?: number | null;
  } = {},
): SanitizedEmployees {
  if (employees === null || employees === undefined) return { employees: null, reason: null };

  if (employees >= 1900 && employees <= 2030) {
    return { employees: null, reason: 'employees_year_leak' };
  }

  // Collision with another number on the same entity — the general form of
  // the mis-tag. Checked before the ceiling because it is definitional and
  // catches values the ceiling cannot see.
  if (employees > COLLISION_MIN_VALUE) {
    // Registration number, compared numerically so leading zeros match
    // (CRN 08252444 vs the parsed integer 8,252,444).
    if (ctx.registryId != null) {
      const digits = String(ctx.registryId).trim().replace(/^0+/, '');
      if (/^\d+$/.test(digits) && Number(digits) === employees) {
        return { employees: null, reason: 'employees_collides_with_own_field' };
      }
    }
    for (const other of [
      ctx.totalAssets,
      ctx.equity,
      ctx.revenue,
      ctx.cashPosition,
      ctx.debtors,
      ctx.creditors,
    ]) {
      if (other != null && other === employees) {
        return { employees: null, reason: 'employees_collides_with_own_field' };
      }
    }
  }
  // The period year written into the headcount slot. Distinct from the
  // 1900-2030 range rule above, which fires regardless of the filing's year.
  if (ctx.periodEndYear != null && ctx.periodEndYear === employees) {
    return { employees: null, reason: 'employees_collides_with_own_field' };
  }

  if (employees > EMPLOYEES_ABSOLUTE_CEILING) {
    return { employees: null, reason: 'employees_beyond_ceiling' };
  }

  const rev = ctx.reportedRevenue ?? null;

  if (
    rev !== null &&
    rev > 0 &&
    employees > 0 &&
    rev / employees > REVENUE_PER_HEAD_IMPOSSIBLE_EUR
  ) {
    return { employees: null, reason: 'employees_partial_count' };
  }

  return { employees, reason: null };
}

// ── 2. Modelled plausibility ceiling ─────────────────────────────────────

/** Modelled turnover at/above this (EUR) is WITHHELD to modelled-pending —
 *  matches the FR/DE model size_cap. A business this size files revenue;
 *  serve nothing until it does (reported-wins fills automatically). */
export const MODELLED_TURNOVER_CEILING_EUR = 120_000_000;

// ── 3. Reported scale-leak fence ─────────────────────────────────────────

/** Generous per-subsegment ceilings (EUR) for REPORTED turnover — set 2-5×
 *  above the largest legitimate operator in each segment, so only
 *  order-of-magnitude parse leaks fire. Keys are canonical-17 labels. */
export const REPORTED_CEILING_EUR: Readonly<Record<string, number>> = {
  'Hospitals & clinics': 25_000_000_000,
  'Pharma wholesale': 40_000_000_000,
  Pharmacy: 15_000_000_000,
  'Medical equipment & supplies': 20_000_000_000,
  'Diagnostics & labs': 10_000_000_000,
  'Specialist medical': 5_000_000_000,
  'Residential elderly care': 5_000_000_000,
  'Residential specialist care': 5_000_000_000,
  'Domiciliary / home care': 5_000_000_000,
  Veterinary: 5_000_000_000,
};
/** Fallback for GP/dental/optical/audiology/physio/ambulance/other — the
 *  small-operator segments where €2bn+ reported IS the Harley pattern. */
export const REPORTED_CEILING_DEFAULT_EUR = 2_000_000_000;

export interface QuarantineInputs {
  turnoverScreener: number | null;
  turnoverBasis: string | null;
  employees: number | null;
  /** EUR-normalised turnover — the ONLY value the ceilings compare against.
   *  Required for the fence to fire: the ceilings are EUR-denominated, so a
   *  native-currency figure is not comparable. (This used to fall back to
   *  turnoverScreener "because FX drift doesn't matter at 2-5× headroom" —
   *  true only while every market was GBP/EUR. At 27 markets it flagged every
   *  weak-currency row as a scale leak: 7.9bn HUF is €19.9M, not €7.9bn.)
   *  Omit it and the fence stays silent rather than guessing. */
  turnoverEur?: number | null;
  totalAssets?: number | null;
  /** Canonical subsegment — selects the reported ceiling. */
  subsegment?: string | null;
}

export interface QuarantineResult {
  quarantined: boolean;
  reasons: QuarantineReason[];
}

/** Evaluate one row. Post-redesign this fences REPORTED scale leaks only —
 *  modelled implausibility is handled upstream (withheld to pending at the
 *  serve), and corrupt inputs are sanitized rather than fenced. */
export function evaluateQuarantine(r: QuarantineInputs): QuarantineResult {
  const reasons: QuarantineReason[] = [];
  const basis = (r.turnoverBasis ?? '').toLowerCase();
  if (basis.includes('dormant')) return { quarantined: false, reasons };

  const reported = basis.includes('reported');
  // EUR only — a native-currency value against a EUR ceiling is meaningless.
  const value = r.turnoverEur;
  if (reported && value !== null && value !== undefined) {
    const ceiling =
      (r.subsegment ? REPORTED_CEILING_EUR[r.subsegment] : undefined) ??
      REPORTED_CEILING_DEFAULT_EUR;
    if (value > ceiling) reasons.push('reported_scale_leak');
  }

  return { quarantined: reasons.length > 0, reasons };
}

const REASON_TEXT: Record<QuarantineReason | OverrideHoldReason | LegacyQuarantineReason, string> =
  {
    reported_scale_leak:
      'Reported turnover exceeds the plausible ceiling for this subsegment by an order of magnitude — almost certainly a filing-parse scale leak.',
    reported_provenance_unverified:
      'Reported figure held pending source confirmation that it is the entity value, not a consolidated group figure.',
    // Retired reasons — still persisted on rows written before the 2026-07-09
    // redesign; display text kept until every market recomputes.
    employees_looks_like_year:
      'The employee count carries a year-like value (likely an iXBRL context-date leak).',
    large_headcount_micro_assets:
      'Disproportionate headcount vs the rest of the balance sheet — input likely corrupted.',
    revenue_per_employee_too_high:
      'Revenue per employee exceeds £5M — the inputs are almost certainly corrupted.',
    revenue_per_employee_too_low:
      'Revenue per employee below £100 — the inputs are almost certainly corrupted.',
    modelled_turnover_implausible:
      'Modelled turnover exceeds €120M — a business this size would file revenue, so the balance-sheet input is almost certainly corrupted (scale error).',
    asset_scale_implausible:
      'Total assets exceed €1bn on a balance-sheet-only filer — a near-certain scale error in the assets input.',
  };

export function describeQuarantine(reasons: string[]): string {
  return reasons
    .map((r) => REASON_TEXT[r as QuarantineReason | LegacyQuarantineReason] ?? r)
    .join(' ');
}
